import { jsPDF } from 'jspdf'; // eslint-disable-line new-cap

const ZONE_DEFINITIONS = [
  { label: '2200-3000', min: 2200, max: 3000 },
  { label: '1800-2200', min: 1800, max: 2200 },
  { label: '1600-1800', min: 1600, max: 1800 },
  { label: '1400-1600', min: 1400, max: 1600 },
  { label: '1200-1400', min: 1200, max: 1400 },
  { label: '800-1200', min: 800, max: 1200 },
  { label: '0-800', min: 0, max: 800 },
];

const SEGMENT_COLORS = [
  '#4472C4', '#ED7D31', '#A9D18E', '#FF4444', '#7030A0',
  '#C00000', '#FF44FF', '#808080', '#808000', '#00B0F0',
  '#FF8C00', '#00B050', '#954F72',
];

const BAR_COLOR = '#4472C4';
const LINE_COLOR = '#0000BB';
const AXIS_COLOR = '#666666';
const GRID_COLOR = '#e0e0e0';
const TEXT_COLOR = '#333333';

// ---- CSV Parsing ----

function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function parseTimestampMs(tsStr) {
  if (!tsStr) return null;
  const d = new Date(tsStr);
  if (!Number.isNaN(d.getTime())) return d.getTime();
  const timeMatch = tsStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (timeMatch) {
    const base = new Date(0);
    base.setUTCHours(parseInt(timeMatch[1], 10), parseInt(timeMatch[2], 10), parseInt(timeMatch[3] || '0', 10), 0);
    return base.getTime();
  }
  const num = parseFloat(tsStr);
  if (!Number.isNaN(num)) return num > 1e10 ? num : num * 1000;
  return null;
}

function msToUtcTimeStr(ms) {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}:${String(d.getUTCSeconds()).padStart(2, '0')}`;
}

const findColIndex = (headers, ...patterns) => patterns.reduce((found, pat) => {
  if (found >= 0) return found;
  return headers.findIndex((h) => new RegExp(pat, 'i').test(h.trim()));
}, -1);

export function parseMetRateCsv(csvText) {
  const lines = csvText.replace(/\r/g, '').trim().split('\n');
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]);

  const tsIdx = findColIndex(headers, 'timestamp', 'time', 'date');
  const mrIdx = findColIndex(headers, 'MetabolicRate_met_rate_btu_hr', 'met_rate_btu', 'met_rate', 'btu_hr', 'metabolic');
  const evIdx = findColIndex(headers, 'ev_num', 'evnum', '^ev$');

  if (mrIdx === -1) return [];

  return lines.slice(1).map((line) => {
    const cols = parseCsvLine(line);
    const mr = parseFloat(cols[mrIdx]);
    if (Number.isNaN(mr) || mr <= 0 || mr > 5000) return null;
    const timeMs = tsIdx >= 0 ? parseTimestampMs(cols[tsIdx]) : null;
    return {
      timeMs,
      metRate: mr,
      evNum: evIdx >= 0 ? (cols[evIdx] || 'EV1') : 'EV1',
    };
  }).filter(Boolean);
}

export function parseSmartTimeline(timelineDataUrl) {
  if (!timelineDataUrl) return [];
  try {
    let text = timelineDataUrl;
    if (typeof text === 'string' && text.startsWith('data:')) {
      const b64 = text.split(',')[1];
      const binary = atob(b64);
      const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
      text = new TextDecoder().decode(bytes);
    }
    const lines = text.replace(/\r/g, '').trim().split('\n');
    if (lines.length < 2) return [];
    const headers = parseCsvLine(lines[0]);

    const actIdx = findColIndex(headers, 'activity', 'task', 'procedure', 'name', 'step');
    const startIdx = findColIndex(headers, 'start');
    const endIdx = findColIndex(headers, 'end', 'stop', 'finish');
    const typeIdx = findColIndex(headers, 'work_item', 'workitem', 'type', 'category');

    if (actIdx === -1 || startIdx === -1) return [];

    return lines.slice(1).map((line) => {
      const cols = parseCsvLine(line);
      const activity = (cols[actIdx] || '').trim();
      if (!activity) return null;
      const startTime = parseTimestampMs(cols[startIdx]);
      const endTime = endIdx >= 0 ? parseTimestampMs(cols[endIdx]) : null;
      const workItemType = typeIdx >= 0 ? (cols[typeIdx] || '').trim() : '';
      return {
        activity, startTime, endTime, workItemType,
      };
    }).filter(Boolean);
  } catch (e) {
    return [];
  }
}

// ---- Statistics ----

function computeStats(data) {
  if (!data.length) return { mean: 0, median: 0, totalBtus: 0 };
  const values = data.map((d) => d.metRate).sort((a, b) => a - b);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const n = values.length;
  const median = n % 2 === 0 ? (values[n / 2 - 1] + values[n / 2]) / 2 : values[Math.floor(n / 2)];

  const totalBtus = data.slice(1).reduce((acc, point, i) => {
    const prev = data[i];
    if (!point.timeMs || !prev.timeMs) return acc;
    const dtHours = (point.timeMs - prev.timeMs) / 3600000;
    if (dtHours > 0 && dtHours < 0.1) return acc + (point.metRate * dtHours);
    return acc;
  }, 0);

  return { mean, median, totalBtus };
}

function computeBoxStats(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const q1 = sorted[Math.floor(n * 0.25)];
  const median = n % 2 === 0 ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2 : sorted[Math.floor(n / 2)];
  const q3 = sorted[Math.floor(n * 0.75)];
  const iqr = q3 - q1;
  const whiskerMin = Math.max(sorted[0], q1 - (1.5 * iqr));
  const whiskerMax = Math.min(sorted[n - 1], q3 + (1.5 * iqr));
  return {
    q1, median, q3, whiskerMin, whiskerMax,
  };
}

function groupDataBySegmentKey(data, segments, keyFn) {
  const groups = {};
  data.forEach((point) => {
    if (!point.timeMs) return;
    const seg = segments.find((s) => s.startTime && s.endTime && point.timeMs >= s.startTime && point.timeMs <= s.endTime);
    if (!seg) return;
    const key = keyFn(seg);
    if (!key) return;
    if (!groups[key]) groups[key] = [];
    groups[key].push(point.metRate);
  });
  return groups;
}

// ---- Canvas Helpers ----

function ctx2dText(ctx, text, x, y, {
  size = 12, bold = false, align = 'left', color = TEXT_COLOR, angle = 0,
} = {}) {
  ctx.save();
  ctx.font = `${bold ? 'bold ' : ''}${size}px Arial`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  if (angle) {
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillText(text, 0, 0);
  } else {
    ctx.fillText(text, x, y);
  }
  ctx.restore();
}

function niceMax(value, ticks = 5) {
  const rough = value / ticks;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const ratio = rough / mag;
  let niceFraction;
  if (ratio <= 1) niceFraction = 1;
  else if (ratio <= 2) niceFraction = 2;
  else if (ratio <= 5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * mag * ticks;
}

// ---- Panel 1: Time Series ----

function drawTimeSeries(ctx, {
  x, y, w, h,
}, data, segments) {
  const ML = 85; const MR = 15; const MT = 55; const MB = 50;
  const px = x + ML; const py = y + MT;
  const pw = w - ML - MR; const ph = h - MT - MB;

  if (!data.length) {
    ctx2dText(ctx, 'No data', x + (w / 2), y + (h / 2), { align: 'center', size: 16 });
    return;
  }

  const withTime = data.filter((d) => d.timeMs);
  const minMs = withTime.length ? Math.min(...withTime.map((d) => d.timeMs)) : 0;
  const maxMs = withTime.length ? Math.max(...withTime.map((d) => d.timeMs)) : 1;
  const mrValues = data.map((d) => d.metRate);
  const minMr = Math.floor(Math.min(...mrValues) / 100) * 100;
  const maxMr = Math.ceil(Math.max(...mrValues) / 100) * 100;
  const mrRange = maxMr - minMr || 1;

  const toX = (ms) => px + (((ms - minMs) / (maxMs - minMs)) * pw);
  const toY = (mr) => py + ph - (((mr - minMr) / mrRange) * ph);

  ctx2dText(ctx, 'Met Rate [BTU/hr]', x + 14, py + (ph / 2), { size: 13, angle: -Math.PI / 2, align: 'center' });

  const bandH = 18;
  const bandY = py - bandH - 8;
  if (segments.length > 0) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(px, bandY - 30, pw, bandH + 30);
    ctx.clip();
    segments.forEach((seg, i) => {
      if (!seg.startTime || !seg.endTime) return;
      const sx = toX(seg.startTime);
      const ex = toX(seg.endTime);
      if (ex <= px || sx >= px + pw) return;
      const bx = Math.max(px, sx);
      const bex = Math.min(px + pw, ex);
      ctx.fillStyle = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
      ctx.fillRect(bx, bandY, bex - bx, bandH);

      const labelX = Math.max(px + 2, Math.min(px + pw - 2, (sx + ex) / 2));
      ctx.save();
      ctx.font = '9px Arial';
      ctx.fillStyle = TEXT_COLOR;
      ctx.textAlign = 'left';
      ctx.translate(labelX, bandY - 4);
      ctx.rotate(-Math.PI / 4);
      const label = seg.activity.length > 20 ? `${seg.activity.substring(0, 18)}...` : seg.activity;
      ctx.fillText(label, 0, 0);
      ctx.restore();
    });
    ctx.restore();
  }

  const yTickCount = 6;
  ctx.strokeStyle = GRID_COLOR;
  ctx.lineWidth = 1;
  Array.from({ length: yTickCount + 1 }, (_, i) => i).forEach((i) => {
    const mr = minMr + (mrRange * (i / yTickCount));
    const yy = toY(mr);
    ctx.beginPath();
    ctx.moveTo(px, yy);
    ctx.lineTo(px + pw, yy);
    ctx.stroke();
    ctx2dText(ctx, Math.round(mr).toString(), px - 5, yy + 4, { align: 'right', size: 11 });
  });

  const totalMs = maxMs - minMs || 1;
  const rawInterval = totalMs / 6;
  const intervals = [60000, 300000, 600000, 900000, 1800000, 3600000];
  const tickIntervalMs = intervals.find((iv) => iv >= rawInterval) || intervals[intervals.length - 1];
  const firstTick = Math.ceil(minMs / tickIntervalMs) * tickIntervalMs;

  for (let ms = firstTick; ms <= maxMs; ms += tickIntervalMs) {
    const xx = toX(ms);
    if (xx >= px && xx <= px + pw) {
      ctx.strokeStyle = GRID_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(xx, py);
      ctx.lineTo(xx, py + ph);
      ctx.stroke();
      ctx.save();
      ctx.font = '11px Arial';
      ctx.fillStyle = TEXT_COLOR;
      ctx.textAlign = 'right';
      ctx.translate(xx + 4, py + ph + 12);
      ctx.rotate(Math.PI / 6);
      ctx.fillText(msToUtcTimeStr(ms), 0, 0);
      ctx.restore();
    }
  }

  ctx.strokeStyle = AXIS_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px, py + ph);
  ctx.lineTo(px + pw, py + ph);
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  ctx.rect(px, py, pw, ph);
  ctx.clip();
  ctx.strokeStyle = LINE_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  let started = false;
  data.forEach((point) => {
    if (!point.timeMs) return;
    const xx = toX(point.timeMs);
    const yy = toY(point.metRate);
    if (!started) { ctx.moveTo(xx, yy); started = true; } else { ctx.lineTo(xx, yy); }
  });
  ctx.stroke();
  ctx.restore();
}

// ---- Panel 2: Histogram ----

function drawHistogram(ctx, {
  x, y, w, h,
}, data, stats) {
  const ML = 60; const MR = 15; const MT = 40; const MB = 50;
  const px = x + ML; const py = y + MT;
  const pw = w - ML - MR; const ph = h - MT - MB;

  const values = data.map((d) => d.metRate);
  if (!values.length) return;

  const minV = Math.floor(Math.min(...values) / 100) * 100;
  const maxV = Math.ceil(Math.max(...values) / 100) * 100;
  const numBins = 15;
  const binSize = (maxV - minV) / numBins || 1;
  const bins = Array(numBins).fill(0);
  values.forEach((v) => {
    const bi = Math.min(Math.floor((v - minV) / binSize), numBins - 1);
    if (bi >= 0) { bins[bi] += 1; }
  });
  const maxCount = niceMax(Math.max(...bins));

  const { mean, median, totalBtus } = stats;
  ctx2dText(ctx,
    `MR: mean=${mean.toFixed(1)}, median=${median.toFixed(1)}, total BTUs: ${totalBtus.toFixed(1)}`,
    x + (w / 2), y + 18, { size: 11, align: 'center' });

  ctx2dText(ctx, 'Freq.', x + 12, py + (ph / 2), { size: 12, angle: -Math.PI / 2, align: 'center' });
  ctx2dText(ctx, 'Met Rate [BTU/hr]', px + (pw / 2), y + h - 8, { size: 12, align: 'center' });

  const yTickCount = 5;
  ctx.strokeStyle = GRID_COLOR;
  ctx.lineWidth = 1;
  Array.from({ length: yTickCount + 1 }, (_, i) => i).forEach((i) => {
    const count = Math.round((maxCount * i) / yTickCount);
    const yy = py + ph - ((count / maxCount) * ph);
    ctx.beginPath();
    ctx.moveTo(px, yy);
    ctx.lineTo(px + pw, yy);
    ctx.stroke();
    ctx2dText(ctx, count.toString(), px - 4, yy + 4, { align: 'right', size: 10 });
  });

  const barW = pw / numBins;
  bins.forEach((count, i) => {
    if (!count) return;
    const bx = px + (i * barW);
    const bh = (count / maxCount) * ph;
    ctx.fillStyle = BAR_COLOR;
    ctx.fillRect(bx + 1, py + ph - bh, barW - 2, bh);
  });

  const tickStep = 3;
  Array.from({ length: Math.floor(numBins / tickStep) + 1 }, (_, i) => i * tickStep).forEach((i) => {
    const v = minV + (i * binSize);
    const xx = px + (i * barW);
    let align = 'center';
    if (i === 0) align = 'left';
    else if (i >= numBins) align = 'right';
    ctx2dText(ctx, Math.round(v).toString(), xx, py + ph + 15, { align, size: 10 });
  });

  ctx.strokeStyle = AXIS_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px, py + ph);
  ctx.lineTo(px + pw, py + ph);
  ctx.stroke();
}

// ---- Panel 3: Zone Chart ----

function drawZoneChart(ctx, {
  x, y, w, h,
}, data) {
  const ML = 90; const MR = 65; const MT = 20; const MB = 40;
  const px = x + ML; const py = y + MT;
  const pw = w - ML - MR; const ph = h - MT - MB;

  const zoneMinutes = ZONE_DEFINITIONS.map(() => 0);
  let total = 0;

  data.slice(1).forEach((point, i) => {
    const prev = data[i];
    if (!point.timeMs || !prev.timeMs) return;
    const dt = (point.timeMs - prev.timeMs) / 60000;
    if (dt <= 0 || dt > 10) return;
    const mr = point.metRate;
    const zIdx = ZONE_DEFINITIONS.findIndex((z) => mr >= z.min && mr < z.max);
    if (zIdx >= 0) {
      zoneMinutes[zIdx] += dt;
      total += dt;
    }
  });

  const maxMinutes = niceMax(Math.max(...zoneMinutes, 1), 5);
  ctx2dText(ctx, 'Time in zone (minutes)', px + (pw / 2), y + h - 8, { size: 12, align: 'center' });

  const rowH = ph / ZONE_DEFINITIONS.length;

  ZONE_DEFINITIONS.forEach((zone, i) => {
    const minutes = zoneMinutes[i];
    const pct = total > 0 ? ((minutes / total) * 100).toFixed(2) : '0.00';
    const barW = (minutes / maxMinutes) * pw;
    const by = py + (i * rowH) + 2;
    const bh = rowH - 4;

    if (barW > 0) {
      ctx.fillStyle = BAR_COLOR;
      ctx.fillRect(px, by, barW, bh);
    }

    ctx2dText(ctx, zone.label, px - 5, by + (bh / 2) + 4, { align: 'right', size: 11 });
    ctx2dText(ctx, `${pct}%`, px + pw + 5, by + (bh / 2) + 4, { size: 11 });

    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(px, by);
    ctx.lineTo(px + pw, by);
    ctx.stroke();
  });

  const xTickCount = 5;
  Array.from({ length: xTickCount + 1 }, (_, i) => i).forEach((i) => {
    const mins = (maxMinutes * i) / xTickCount;
    const xx = px + ((mins / maxMinutes) * pw);
    ctx2dText(ctx, Math.round(mins).toString(), xx, py + ph + 15, { align: 'center', size: 10 });
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xx, py);
    ctx.lineTo(xx, py + ph);
    ctx.stroke();
  });

  ctx.strokeStyle = AXIS_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px, py + ph);
  ctx.lineTo(px + pw, py + ph);
  ctx.stroke();
}

// ---- Panels 4 & 5: Box Plots ----

function drawBoxPlots(ctx, {
  x, y, w, h,
}, groups, title) {
  const ML = 65; const MR = 15; const MT = 25; const MB = 90;
  const px = x + ML; const py = y + MT;
  const pw = w - ML - MR; const ph = h - MT - MB;

  ctx2dText(ctx, title, px + (pw / 2), y + 16, { size: 12, bold: true, align: 'center' });

  const keys = Object.keys(groups);
  if (!keys.length) {
    ctx2dText(ctx, 'Upload SmartTimeline for activity breakdown',
      x + (w / 2), y + (h / 2), { align: 'center', size: 11, color: '#999999' });
    return;
  }

  ctx2dText(ctx, 'Met Rate [BTU/hr]', x + 14, py + (ph / 2), { size: 11, angle: -Math.PI / 2, align: 'center' });

  const allValues = keys.flatMap((k) => groups[k]);
  const minV = Math.floor(Math.min(...allValues) / 100) * 100;
  const maxV = Math.ceil(Math.max(...allValues) / 100) * 100;
  const vRange = maxV - minV || 1;

  const toY = (v) => py + ph - (((v - minV) / vRange) * ph);

  const yTickCount = 5;
  ctx.strokeStyle = GRID_COLOR;
  ctx.lineWidth = 1;
  Array.from({ length: yTickCount + 1 }, (_, i) => i).forEach((i) => {
    const v = minV + (vRange * (i / yTickCount));
    const yy = toY(v);
    ctx.beginPath();
    ctx.moveTo(px, yy);
    ctx.lineTo(px + pw, yy);
    ctx.stroke();
    ctx2dText(ctx, Math.round(v).toString(), px - 4, yy + 4, { align: 'right', size: 10 });
  });

  const step = pw / keys.length;
  const boxW = Math.min(35, step * 0.5);

  keys.forEach((key, i) => {
    const stats = computeBoxStats(groups[key]);
    if (!stats) return;
    const cx = px + (step * i) + (step / 2);
    const {
      q1, median, q3, whiskerMin, whiskerMax,
    } = stats;

    const y1 = toY(q1);
    const y3 = toY(q3);
    const ym = toY(median);
    const yWMin = toY(whiskerMin);
    const yWMax = toY(whiskerMax);

    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(cx - (boxW / 2), y3, boxW, y1 - y3);

    ctx.strokeStyle = '#ED7D31';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx - (boxW / 2), ym);
    ctx.lineTo(cx + (boxW / 2), ym);
    ctx.stroke();

    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1.5;
    [[y1, yWMin], [y3, yWMax]].forEach(([boxEdge, whisker]) => {
      ctx.beginPath();
      ctx.moveTo(cx, boxEdge);
      ctx.lineTo(cx, whisker);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - (boxW / 4), whisker);
      ctx.lineTo(cx + (boxW / 4), whisker);
      ctx.stroke();
    });

    ctx.save();
    ctx.font = '10px Arial';
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = 'right';
    ctx.translate(cx + 4, py + ph + 10);
    ctx.rotate(Math.PI / 4);
    const label = key.length > 20 ? `${key.substring(0, 18)}...` : key;
    ctx.fillText(label, 0, 0);
    ctx.restore();
  });

  ctx.strokeStyle = AXIS_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px, py + ph);
  ctx.lineTo(px + pw, py + ph);
  ctx.stroke();
}

// ---- Main Export ----

export async function generateFodPlotPdf({ metRateCsvText, timelineDataUrl, title }) {
  const data = parseMetRateCsv(metRateCsvText);
  if (!data.length) throw new Error('No valid met rate data found in CSV');

  const segments = parseSmartTimeline(timelineDataUrl);
  const sorted = [...data].sort((a, b) => (a.timeMs || 0) - (b.timeMs || 0));
  const stats = computeStats(sorted);

  const W = 1600;
  const H = 1050;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  ctx2dText(ctx, title, W / 2, 28, { size: 20, bold: true, align: 'center' });

  drawTimeSeries(ctx, {
    x: 10, y: 45, w: W - 20, h: 270,
  }, sorted, segments);
  drawHistogram(ctx, {
    x: 10, y: 335, w: (W / 2) - 15, h: 250,
  }, sorted, stats);
  drawZoneChart(ctx, {
    x: (W / 2) + 5, y: 335, w: (W / 2) - 15, h: 250,
  }, sorted);

  const taskGroups = segments.length > 0
    ? groupDataBySegmentKey(sorted, segments, (s) => s.activity)
    : {};
  const workGroups = segments.length > 0
    ? groupDataBySegmentKey(sorted, segments, (s) => s.workItemType || s.activity)
    : {};

  drawBoxPlots(ctx, {
    x: 10, y: 605, w: (W / 2) - 15, h: 270,
  }, taskGroups, 'Met Rate by Task');
  drawBoxPlots(ctx, {
    x: (W / 2) + 5, y: 605, w: (W / 2) - 15, h: 270,
  }, workGroups, 'Met Rate by Work Item Type');

  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    hotfixes: ['px_scaling'],
    format: [W, H],
  });
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, W, H);
  return pdf.output('blob');
}

const SPECIAL_LINK_KEYS = ['requestingData', 'replayingData', 'cancelingDataReplay', 'error', 'message', 'warning'];

export function pickMetRateKey(links) {
  const candidates = Object.keys(links || {}).filter((k) => !SPECIAL_LINK_KEYS.includes(k) && links[k]);
  if (!candidates.length) return null;
  const scored = candidates
    .map((key) => {
      let score = 0;
      if (/met[_\s-]*rate/i.test(key)) score += 4;
      if (/hhp|metabolic/i.test(key)) score += 3;
      if (/model/i.test(key)) score += 1;
      if (/heart|humid|temp|ecs|suit/i.test(key)) score -= 5;
      return { key, score };
    })
    .sort((a, b) => b.score - a.score);
  const [best] = scored;
  return best.score > 0 ? best.key : candidates[0];
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadFodPlotFromCsvUrl({
  csvUrl, timelineDataUrl = null, title, filename,
}) {
  const resp = await fetch(csvUrl);
  if (!resp.ok) throw new Error(`Failed to fetch data: ${resp.status}`);
  const csvText = await resp.text();
  const pdfBlob = await generateFodPlotPdf({ metRateCsvText: csvText, timelineDataUrl, title });
  triggerBlobDownload(pdfBlob, filename);
}
